import React from 'react';

/**
 * Badge Component
 *
 * A reusable badge/pill for status indicators, risk levels, and labels.
 * Uses design tokens from tailwind.config.js.
 *
 * @example
 * // Risk level badges (most common use case)
 * <Badge variant="risk-high">High</Badge>
 * <Badge variant="risk-medium">Medium</Badge>
 * <Badge variant="risk-low">Low</Badge>
 *
 * // Auto-detect risk level from children text
 * <Badge variant="risk">{driver.risk_level}</Badge>
 *
 * // Other variants
 * <Badge variant="info">Active</Badge>
 * <Badge variant="neutral">Pending</Badge>
 *
 * // With dot indicator
 * <Badge variant="risk-high" dot>High Risk</Badge>
 *
 * // Sizes
 * <Badge size="sm">Small</Badge>
 * <Badge size="md">Medium</Badge>
 */

const Badge = ({
  children,
  variant = 'neutral',
  size = 'sm',
  dot = false,
  className = '',
  ...props
}) => {
  // If variant is "risk", auto-detect from children text
  const resolvedVariant = variant === 'risk'
    ? getRiskVariant(children)
    : variant;

  // Base styles
  const baseStyles = `
    inline-flex items-center gap-1.5
    font-medium rounded-full
    border
  `;

  // Variant styles using design tokens
  const variants = {
    'risk-high': `
      bg-risk-high-50
      text-risk-high-700
      border-risk-high-200
    `,
    'risk-medium': `
      bg-risk-medium-50
      text-risk-medium-600
      border-risk-medium-200
    `,
    'risk-low': `
      bg-risk-low-50
      text-risk-low-700
      border-risk-low-200
    `,
    'info': `
      bg-brand-50
      text-brand-700
      border-brand-200
    `,
    'neutral': `
      bg-neutral-100
      text-neutral-700
      border-neutral-200
    `,
  };

  // Size styles
  const sizes = {
    xs: 'px-1.5 py-0.5 text-[10px]',
    sm: 'px-2 py-0.5 text-[11px]',
    md: 'px-2.5 py-1 text-xs',
  };

  // Dot colors for each variant
  const dotColors = {
    'risk-high': 'bg-risk-high-600',
    'risk-medium': 'bg-risk-medium-500',
    'risk-low': 'bg-risk-low-600',
    'info': 'bg-brand-600',
    'neutral': 'bg-neutral-400',
  };

  return (
    <span
      className={`
        ${baseStyles}
        ${variants[resolvedVariant] || variants.neutral}
        ${sizes[size]}
        ${className}
      `}
      {...props}
    >
      {dot && (
        <span
          className={`w-1.5 h-1.5 rounded-full ${dotColors[resolvedVariant] || dotColors.neutral}`}
        />
      )}
      {children}
    </span>
  );
};

/**
 * Helper to auto-detect risk variant from text
 */
function getRiskVariant(text) {
  const normalized = String(text).toLowerCase().trim();

  if (normalized === 'high') return 'risk-high';
  if (normalized === 'medium' || normalized === 'moderate') return 'risk-medium';
  if (normalized === 'low') return 'risk-low';

  return 'neutral';
}

/**
 * Convenience component for risk level badges
 * Automatically styles based on the risk level text
 *
 * @example
 * <RiskBadge level="high" />
 * <RiskBadge level={driver.risk_level} />
 */
export const RiskBadge = ({ level, size = 'sm', dot = false, className = '' }) => {
  if (!level) return null;

  return (
    <Badge
      variant="risk"
      size={size}
      dot={dot}
      className={className}
    >
      {level}
    </Badge>
  );
};

export default Badge;
